import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import {
  Sun,
  BatteryCharging,
  Lock,
  Leaf,
  Linkedin,
  Instagram,
  ArrowRight,
  ShieldCheck,
  Languages,
  CalendarCheck,
  MapPin,
  Award,
  Zap,
  PiggyBank,
  FileCheck,
  Mail,
  Phone,
  Clock,
  CalendarDays,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { ChatWidget } from "@/components/ChatWidget";
import { BookingSection } from "@/components/BookingSection";
import { faqCategories } from "@/data/faqData";

const HERO_IMG =
  "https://images.unsplash.com/photo-1761472823286-9f6093ed6663?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwxfHxzb2xhciUyMHBhbmVscyUyMG1vZGVybiUyMHJvb2Z8ZW58MHx8fHwxNzg0MDIwMDczfDA&ixlib=rb-4.1.0&q=85";
const BATTERY_IMG =
  "https://images.unsplash.com/photo-1780445392417-68b9dccc45f2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwxfHxiYXR0ZXJ5JTIwc3RvcmFnZSUyMGhvbWV8ZW58MHx8fHwxNzg0MDIwMDczfDA&ixlib=rb-4.1.0&q=85";
const NATURE_IMG =
  "https://images.unsplash.com/photo-1545840893-d59d0bf89825?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwzfHxjbGVhbiUyMGVuZXJneSUyMG5hdHVyZXxlbnwwfHx8fDE3ODQwMjAwNzN8MA&ixlib=rb-4.1.0&q=85";

const fade = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] },
};

const Header = () => {
  const [count, setCount] = useState(0);
  const [authed, setAuthed] = useState(false);
  useEffect(() => {
    api
      .get("/auth/me")
      .then(() => {
        setAuthed(true);
        api.get("/appointments").then(({ data }) => setCount(data.length)).catch(() => {});
      })
      .catch(() => setAuthed(false));
  }, []);
  return (
  <header className="fixed top-0 z-40 w-full border-b border-border/60 bg-background/70 backdrop-blur-xl">
    <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
      <a href="#top" data-testid="brand-logo" className="flex items-center gap-2 font-display text-xl font-bold tracking-tight">
        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary text-primary-foreground">
          <Sun className="h-5 w-5" />
        </span>
        Solaria
        <span className="hidden text-xs font-medium text-muted-foreground sm:inline">par Euroconsult</span>
      </a>
      <nav className="hidden items-center gap-7 text-sm font-medium lg:flex">
        <a href="#offre" className="transition-colors hover:text-accent">L'offre</a>
        <a href="#avantages" className="transition-colors hover:text-accent">Avantages</a>
        <a href="#installation" className="transition-colors hover:text-accent">Installation</a>
        <a href="#faq" className="transition-colors hover:text-accent">FAQ</a>
        <Link to="/comparateur" data-testid="nav-comparateur-link" className="transition-colors hover:text-accent">Comparateur</Link>
        <Link to="/contrat" data-testid="nav-contrat-link" className="transition-colors hover:text-accent">Contrat</Link>
        {authed && (
          <Link to="/agenda" data-testid="nav-agenda-link" className="transition-colors hover:text-accent">Agenda</Link>
        )}
      </nav>
      <div className="flex items-center gap-3">
        {authed && (
          <Link to="/agenda" data-testid="header-agenda-badge" aria-label="Voir l'agenda" className="relative flex h-9 w-9 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-accent">
            <CalendarDays className="h-5 w-5" />
            {count > 0 && (
              <span data-testid="header-agenda-count" className="absolute -right-1 -top-1 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-accent px-1 text-[10px] font-bold text-accent-foreground">
                {count}
              </span>
            )}
          </Link>
        )}
        <a href="https://www.linkedin.com/company/136147309/" target="_blank" rel="noreferrer" data-testid="header-linkedin" aria-label="LinkedIn Solaria" className="hidden text-muted-foreground transition-colors hover:text-accent sm:block">
          <Linkedin className="h-5 w-5" />
        </a>
        <a href="https://instagram.com" target="_blank" rel="noreferrer" data-testid="header-instagram" aria-label="Instagram Solaria" className="hidden text-muted-foreground transition-colors hover:text-accent sm:block">
          <Instagram className="h-5 w-5" />
        </a>
        <Button asChild size="sm" className="rounded-full bg-accent hover:bg-accent/90">
          <a href="#rendez-vous" data-testid="header-cta">Rendez-vous</a>
        </Button>
      </div>
    </div>
  </header>
  );
};

export default function Home() {
  return (
    <div id="top" className="relative overflow-hidden">
      <Header />

      {/* HERO */}
      <section className="relative px-6 pb-20 pt-36 md:pt-44" aria-label="Installation photovoltaïque en Wallonie et en Flandre">
        <div className="mx-auto grid max-w-6xl items-center gap-12 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <motion.span
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em]"
            >
              <MapPin className="h-3.5 w-3.5 text-accent" /> Installateur photovoltaïque · Wallonie & Flandre
            </motion.span>
            <h1 className="mt-6 font-display text-4xl font-extrabold leading-[0.98] tracking-tighter sm:text-5xl lg:text-6xl">
              Panneaux solaires + batterie sur mesure en Wallonie & Flandre dès{" "}
              <span className="text-accent">150 €</span>
            </h1>
            <p className="mt-6 max-w-lg text-lg leading-relaxed text-muted-foreground">
              Solaria (Euroconsult - Consultant en Energie) installe vos{" "}
              <strong className="text-foreground">panneaux solaires et vos batteries domestiques</strong>{" "}
              en Wallonie et en Flandre, selon votre consommation.
            </p>
            <p className="mt-4 max-w-lg text-lg leading-relaxed text-muted-foreground">
              <strong className="text-foreground">Particulier ou en Société</strong>, Nous intégrons{" "}
              <strong className="text-foreground">une ou plusieurs batteries</strong> parfaitement adaptées à vos besoins.
            </p>
            <p className="mt-4 max-w-lg text-lg leading-relaxed text-muted-foreground">
              Votre tarif d'électricité reste{" "}
              <span className="font-semibold text-foreground">bloqué à 0,27 €/kWh pendant 25 ans</span>.
            </p>
            <p className="mt-4 inline-flex items-center gap-2 rounded-full bg-secondary/50 px-4 py-1.5 text-sm font-medium text-foreground" data-testid="hero-owners-note">
              <ShieldCheck className="h-4 w-4 text-accent" /> Offre réservée aux propriétaires
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Button asChild size="lg" className="group h-14 rounded-full bg-accent px-8 text-base hover:bg-accent/90">
                <a href="#rendez-vous" data-testid="hero-cta-button">
                  Réserver mon étude gratuite
                  <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                </a>
              </Button>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Languages className="h-4 w-4 text-accent" /> Assistante IA multilingue 24/7
              </div>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="relative lg:col-span-6"
          >
            <img
              src={HERO_IMG}
              alt="Installation de panneaux solaires photovoltaïques sur toit en Wallonie et en Flandre - Solaria Euroconsult"
              className="aspect-[4/5] w-full rounded-[2rem] object-cover shadow-2xl"
              width="800" height="1000" loading="eager"
            />
            <div className="absolute -bottom-6 -left-6 flex items-center gap-4 rounded-2xl border border-border bg-card/95 p-5 shadow-xl backdrop-blur">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary text-primary-foreground">
                <Lock className="h-6 w-6" />
              </div>
              <div>
                <p className="font-display text-2xl font-bold leading-none">0,27 €<span className="text-sm font-medium text-muted-foreground">/kWh</span></p>
                <p className="text-xs text-muted-foreground">Bloqué durant 25 ans</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* OFFER BENTO */}
      <section id="offre" className="px-6 py-24 md:py-28">
        <div className="mx-auto max-w-6xl">
          <motion.div {...fade} className="mb-14 max-w-2xl">
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-accent">L'offre</span>
            <h2 className="mt-4 text-4xl font-bold tracking-tighter sm:text-5xl">Offre Solaria (Euroconsult)</h2>
            <p className="mt-4 text-base leading-relaxed text-muted-foreground">
              Une solution photovoltaïque tout compris pour les ménages belges : panneaux solaires, batterie
              domestique, installation et certification — en un seul forfait clair.
            </p>
          </motion.div>
          <div className="grid gap-6 md:grid-cols-12">
            <motion.div {...fade} className="flex flex-col justify-between rounded-3xl bg-primary p-8 text-primary-foreground md:col-span-7 md:row-span-2">
              <BatteryCharging className="h-10 w-10 text-secondary" />
              <div className="mt-8">
                <img src={BATTERY_IMG} alt="Batterie domestique de stockage d'énergie solaire installée en Wallonie et en Flandre" className="mb-6 aspect-video w-full rounded-2xl object-cover" width="700" height="394" loading="lazy" />
                <h3 className="font-display text-3xl font-bold tracking-tight">Panneaux + batterie inclus</h3>
                <p className="mt-3 max-w-md leading-relaxed text-primary-foreground/80">
                  Une installation photovoltaïque complète avec batterie de stockage, pour consommer votre propre
                  énergie jour et nuit et maximiser votre autoconsommation.
                </p>
              </div>
            </motion.div>

            <motion.div {...fade} className="flex flex-col justify-center rounded-3xl border border-border bg-card p-8 md:col-span-5">
              <span className="font-display text-6xl font-extrabold tracking-tighter text-accent">150 €</span>
              <p className="mt-2 text-lg font-semibold">Frais de dossier uniques</p>
              <p className="mt-1 text-sm text-muted-foreground">Sans investissement. Installation, contrôle et suivi compris.</p>
            </motion.div>

            <motion.div {...fade} className="flex items-center gap-5 rounded-3xl border border-border bg-card p-8 md:col-span-5">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-secondary text-secondary-foreground">
                <Lock className="h-7 w-7" />
              </div>
              <div>
                <h3 className="font-display text-xl font-bold">Tarif bloqué 25 ans</h3>
                <p className="text-sm text-muted-foreground">0,27 €/kWh garanti, à l'abri des hausses.</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ADVANTAGES */}
      <section id="avantages" className="border-y border-border bg-muted/40 px-6 py-24">
        <div className="mx-auto max-w-6xl">
          <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
            <motion.div {...fade}>
              <img src={NATURE_IMG} alt="Énergie solaire propre et autoconsommation en Wallonie et en Flandre" className="aspect-square w-full rounded-[2rem] object-cover shadow-xl" width="800" height="800" loading="lazy" />
            </motion.div>
            <motion.div {...fade}>
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-accent">Avantages</span>
              <h2 className="mt-4 text-4xl font-bold tracking-tighter sm:text-5xl">Avantages de l'installation photovoltaïque</h2>
              <div className="mt-8 space-y-6">
                {[
                  { icon: PiggyBank, t: "Réduisez votre facture d'énergie", d: "Jusqu'à 90 % d'autoconsommation en moyenne : vous produisez et stockez votre propre électricité verte." },
                  { icon: ShieldCheck, t: "Zéro mauvaise surprise", d: "Tarif fixe 0,27 €/kWh pendant 25 ans, inflation plafonnée à 2 %/an, et l'installation vous appartient ensuite." },
                  { icon: Zap, t: "Indépendance & sécurité", d: "Batterie avec back-up : votre habitation continue de fonctionner même en cas de coupure sur le réseau." },
                  { icon: Leaf, t: "Un geste pour la planète", d: "Une énergie locale et renouvelable, produite sur votre toit en Wallonie et en Flandre." },
                ].map((x, i) => (
                  <div key={i} className="flex gap-4">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
                      <x.icon className="h-5 w-5" />
                    </div>
                    <div>
                      <h3 className="font-display text-lg font-bold">{x.t}</h3>
                      <p className="text-muted-foreground">{x.d}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* LOCKED PRICE */}
      <section id="tarif" className="px-6 py-24 md:py-28">
        <div className="mx-auto max-w-6xl overflow-hidden rounded-[2rem] bg-primary p-10 text-primary-foreground md:p-16">
          <div className="grid items-center gap-10 lg:grid-cols-2">
            <motion.div {...fade}>
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-secondary">Tarif garanti</span>
              <h2 className="mt-4 text-4xl font-bold tracking-tighter sm:text-5xl">Tarif bloqué 0,27 €/kWh pendant 25 ans</h2>
              <p className="mt-5 max-w-lg leading-relaxed text-primary-foreground/80">
                Pendant que les prix de l'électricité grimpent en Wallonie et en Flandre, votre tarif reste bloqué à 0,27 €/kWh —
                pour toute votre consommation, pendant 25 ans. L'indexation est plafonnée à 2 % par an, et au terme du
                contrat, l'installation vous appartient entièrement.
              </p>
            </motion.div>
            <motion.div {...fade} className="grid grid-cols-2 gap-4">
              {[
                { v: "0,27 €", l: "par kWh, bloqué" },
                { v: "25 ans", l: "de tarif garanti" },
                { v: "2 %", l: "d'indexation max/an" },
                { v: "150 €", l: "frais de dossier uniques" },
              ].map((s, i) => (
                <div key={i} className="rounded-2xl bg-white/5 p-6">
                  <p className="font-display text-3xl font-extrabold tracking-tighter text-secondary">{s.v}</p>
                  <p className="mt-1 text-sm text-primary-foreground/70">{s.l}</p>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE */}
      <section id="pourquoi" className="border-y border-border bg-muted/40 px-6 py-24">
        <div className="mx-auto max-w-6xl">
          <motion.div {...fade} className="mb-12 max-w-2xl">
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-accent">Confiance</span>
            <h2 className="mt-4 text-4xl font-bold tracking-tighter sm:text-5xl">Pourquoi choisir Solaria ?</h2>
          </motion.div>
          <div className="grid gap-6 md:grid-cols-3">
            {[
              { icon: MapPin, t: "Wallonie & Flandre", d: "Nous sommes actuellement présents en Wallonie et en Flandre, avec étude gratuite à domicile." },
              { icon: Award, t: "Installateur indépendant", d: "Solaria (Euroconsult), un interlocuteur unique du devis au suivi, sans intermédiaire." },
              { icon: FileCheck, t: "Tout compris & certifié", d: "Installation, contrôle par organisme agréé et démarches administratives inclus." },
              { icon: Languages, t: "Assistance IA multilingue", d: "Léa répond dans votre langue 24/7 et prépare votre rendez-vous." },
              { icon: ShieldCheck, t: "Prix transparent", d: "150 € de frais de dossier, 0,27 €/kWh bloqué 25 ans. Pas de frais cachés." },
              { icon: PiggyBank, t: "Économies durables", d: "Comparez en ligne vos économies grâce à notre comparateur d'énergie." },
            ].map((x, i) => (
              <motion.div key={i} {...fade} className="rounded-3xl border border-border bg-card p-7">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-secondary text-secondary-foreground">
                  <x.icon className="h-6 w-6" />
                </div>
                <h3 className="mt-5 font-display text-lg font-bold">{x.t}</h3>
                <p className="mt-2 text-muted-foreground">{x.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* INSTALLATION & CERTIFICATION */}
      <section id="installation" className="px-6 py-24 md:py-28">
        <div className="mx-auto max-w-6xl">
          <motion.div {...fade} className="mb-12 max-w-2xl">
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-accent">Processus</span>
            <h2 className="mt-4 text-4xl font-bold tracking-tighter sm:text-5xl">Installation & certification en Wallonie et en Flandre</h2>
            <p className="mt-4 text-base leading-relaxed text-muted-foreground">
              De l'étude gratuite à la mise en service, Solaria gère tout — y compris le contrôle de conformité (RGIE)
              par un organisme agréé.
            </p>
          </motion.div>
          <div className="grid gap-6 md:grid-cols-4">
            {[
              { n: "1", t: "Étude gratuite", d: "Rendez-vous sans engagement pour analyser votre toiture, votre consommation et votre situation." },
              { n: "2", t: "Devis clair", d: "Un devis transparent, adapté à votre logement. Dès l'acompte, nous planifions l'installation." },
              { n: "3", t: "Installation", d: "Pose des panneaux et de la batterie par nos équipes, en Wallonie et en Flandre." },
              { n: "4", t: "Certification", d: "Contrôle par organisme agréé, conformité RGIE et démarches administratives prises en charge." },
            ].map((s, i) => (
              <motion.div key={i} {...fade} className="rounded-3xl border border-border bg-card p-7">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-accent font-display text-lg font-bold text-accent-foreground">{s.n}</span>
                <h3 className="mt-5 font-display text-lg font-bold">{s.t}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{s.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <BookingSection />

      {/* FAQ */}
      <section id="faq" className="px-6 py-24 md:py-32">
        <div className="mx-auto max-w-3xl">
          <motion.div {...fade} className="mb-12">
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-accent">FAQ</span>
            <h2 className="mt-4 text-4xl font-bold tracking-tighter sm:text-5xl">FAQ photovoltaïque Wallonie & Flandre</h2>
            <p className="mt-3 text-muted-foreground">Notre assistante IA répond aussi dans votre langue, à tout moment.</p>
          </motion.div>
          <div className="space-y-10" data-testid="faq-accordion">
            {faqCategories.map((cat, ci) => (
              <div key={ci} data-testid={`faq-category-${ci}`}>
                <h3 className="mb-2 text-xs font-semibold uppercase tracking-[0.2em] text-accent">
                  {cat.category}
                </h3>
                <Accordion type="single" collapsible className="w-full">
                  {cat.items.map((f, i) => (
                    <AccordionItem key={i} value={`c${ci}-i${i}`} data-testid={`faq-item-${ci}-${i}`}>
                      <AccordionTrigger className="text-left font-display text-base font-semibold">
                        {f.q}
                      </AccordionTrigger>
                      <AccordionContent className="text-base leading-relaxed text-muted-foreground">
                        {f.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-border bg-primary px-6 py-16 text-primary-foreground">
        <div className="mx-auto flex max-w-6xl flex-col items-start justify-between gap-10 md:flex-row md:items-start">
          <div>
            <div className="flex items-center gap-2 font-display text-2xl font-bold">
              <Sun className="h-6 w-6 text-accent" /> Solaria
            </div>
            <p className="mt-3 max-w-sm text-primary-foreground/70">
              Solaria par <strong>Euroconsult</strong> - Consultant en installations Photovoltaïques en Wallonie et en Flandre.
              <br />
              Panneaux solaires, batteries domestiques et professionnelles et énergie à un prix maîtrisé.
            </p>
            <div className="mt-5 space-y-1.5 text-sm text-primary-foreground/70" data-testid="footer-legal">
              <p className="font-semibold text-primary-foreground">Euroconsult SNC</p>
              <p className="flex items-center gap-2"><MapPin className="h-4 w-4 shrink-0 text-secondary" /> Rue des Chanterelles 13, 1470 Genappe</p>
              <p>TVA : BE 0644.763.354</p>
              <a href="tel:+32472195245" className="flex items-center gap-2 transition-colors hover:text-secondary" data-testid="footer-phone">
                <Phone className="h-4 w-4 shrink-0 text-secondary" /> 0472 19 52 45
              </a>
              <a href="mailto:euroconsult.eu@gmail.com" className="flex items-center gap-2 transition-colors hover:text-secondary" data-testid="footer-email">
                <Mail className="h-4 w-4 shrink-0 text-secondary" /> euroconsult.eu@gmail.com
              </a>
              <p className="flex items-center gap-2"><Clock className="h-4 w-4 shrink-0 text-secondary" /> Du lundi au vendredi : 9h30 – 18h</p>
              <p className="flex items-center gap-2"><MapPin className="h-4 w-4 shrink-0 text-secondary" /> Wallonie · Flandre</p>
            </div>
          </div>
          <div className="flex flex-col gap-4">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary-foreground/60">Suivez-moi</p>
            <div className="flex gap-4">
              <a href="https://www.linkedin.com/company/136147309/" target="_blank" rel="noreferrer" data-testid="footer-linkedin" aria-label="LinkedIn" className="flex h-12 w-12 items-center justify-center rounded-full bg-white/10 transition-colors hover:bg-accent">
                <Linkedin className="h-5 w-5" />
              </a>
              <a href="https://instagram.com" target="_blank" rel="noreferrer" data-testid="footer-instagram" aria-label="Instagram" className="flex h-12 w-12 items-center justify-center rounded-full bg-white/10 transition-colors hover:bg-accent">
                <Instagram className="h-5 w-5" />
              </a>
            </div>
          </div>
        </div>
        <div className="mx-auto mt-12 flex max-w-6xl flex-col gap-3 border-t border-white/10 pt-6 text-sm text-primary-foreground/50 sm:flex-row sm:items-center sm:justify-between">
          <span>© {new Date().getFullYear()} Solaria — Euroconsult. Installation photovoltaïque en Wallonie et en Flandre. Tous droits réservés.</span>
          <div className="flex items-center gap-5">
            <Link to="/confidentialite" data-testid="footer-privacy-link" className="transition-colors hover:text-secondary">
              Politique de confidentialité
            </Link>
            <Link to="/login" data-testid="footer-login-link" className="inline-flex items-center gap-1.5 transition-colors hover:text-secondary">
              <Lock className="h-3.5 w-3.5" /> Espace pro
            </Link>
          </div>
        </div>
      </footer>

      <ChatWidget />
    </div>
  );
}

